import mixpanel from 'mixpanel-browser';
import {
    getScorecardEventProperties,
    getClauseLibraryEventProperties,
    getContractsEventProperties,
    getLabelsEventProperties,
    getMarkupEventProperties
} from '@Utils/mixpanelUtils';
/** { ip: false } means that Mixpanel will not collect the user's IP address.
 * Now all events will not inculde the following properties: city, region, and country.
 * For more details: https://help.mixpanel.com/hc/en-us/articles/115004494803-Disable-Geolocation-Collection
 * Mixpanel full API reference: https://developer.mixpanel.com/docs/javascript-full-api-reference
 */

/*
    // Environment URLs
    // https://analyzer.insight.docusign.com            PROD
    // https://analyzer.insight-s.docusign.com          PRE-PROD
    // https://analyzer.insight-d.docusign.com          DEMO
    // https://analyzer.insight-hqtest3-dev.seal-software.tech   DEV/QA
    // https://localhost:3000   DEV
    // https://atn-ojs-qa2.seal-software.net    PRE-PROD
    // https://atn-ojs-qa1.seal-software.net    QA
*/
const serverDomain = window.location.origin;
if (
    serverDomain.indexOf('scs-atn.seal-software.com') > -1 ||
    serverDomain.indexOf('analyzer.insight.docusign.com') > -1
) {
    // PROD
    mixpanel.init('a975e6ab82bbb08943c49b281c34d2ca', { ip: false }); // "Analyzer Prod" project token
} else {
    // TEST
    mixpanel.init('ad8acecca68ff994fdb6c82cf1e66ff7', { ip: false }); // "Analyzer Test" project token
}

const mixpanelEvents = {
    logIn: (superProperties, eventProperties) => {
        mixpanel.register({
            ...superProperties,
            Component: 'Analyzer',
            Application: 'Word'
        });
        mixpanel.track('Login', eventProperties);
    },
    menuPageNavigation: eventProperties => {
        try {
            mixpanel.track('Menu page navigation', eventProperties);
        } catch (e) {
            console.log('error', e);
        }
    },
    menuDisplayHelpInformation: eventProperties => {
        mixpanel.track('Menu display help information', eventProperties);
    },
    menuOther: eventProperties => {
        mixpanel.track('Menu other', eventProperties);
    },
    scorecardSelectorItemSelected: eventProperties => {
        mixpanel.track('Scorecard Selector item selected', eventProperties);
    },
    docAnalysis: eventProperties => {
        mixpanel.track('Analyze button clicked', eventProperties);
        mixpanel.people.increment('All Time Document Analysis');
    },
    docCancelAnalyzing: eventProperties => {
        mixpanel.track('Analyze cancel button clicked', eventProperties);
    },
    startPageDisplayed: () => {
        mixpanel.track('Start Page Displayed', getScorecardEventProperties('Start Page Displayed'));
    },
    calculateScorecardRenderTime: () => {
        mixpanel.track(
            'Scorecard render time',
            getScorecardEventProperties('Scorecard render time')
        );
    },
    scorecardFiltersBarChart: () => {
        mixpanel.track(
            'Scorecard filters barchart',
            getScorecardEventProperties('Scorecard filters barchart')
        );
    },
    scorecardFiltersApplied: () => {
        mixpanel.track(
            'Scorecard filters applied',
            getScorecardEventProperties('Scorecard filters applied')
        );
    },
    scorecardFiltersReset: () => {
        mixpanel.track(
            'Scorecard filters reset',
            getScorecardEventProperties('Scorecard filters reset')
        );
    },
    ruleCardClickedShowOnDocument: () => {
        mixpanel.track(
            'Rule card clicked Show on Document',
            getScorecardEventProperties('Rule card clicked Show on Document')
        );
    },
    insertlinkedclause: () => {
        mixpanel.track('Insert linked clause', getScorecardEventProperties('Insert linked clause'));
    },
    showLinkedClauses: () => {
        mixpanel.track('Show linked clauses', getScorecardEventProperties('Show linked clauses'));
    },
    detailedClauseViewInsertClause: () => {
        mixpanel.track(
            'Detailed clause view Insert clause',
            getClauseLibraryEventProperties('Detailed clause view Insert clause')
        );
    },
    clauselibraryDisplayed: () => {
        mixpanel.track(
            'Clause library Displayed',
            getClauseLibraryEventProperties('Clause library Displayed')
        );
    },
    clauseLibraryFilterApplied: () => {
        mixpanel.track(
            'Clause library filter applied',
            getClauseLibraryEventProperties('Clause library filter applied')
        );
    },
    clauseLibraryExpandGroup: () => {
        mixpanel.track(
            'Clause library Expand group',
            getClauseLibraryEventProperties('Clause library Expand group')
        );
    },
    clauseLibraryShowClauseDetails: () => {
        mixpanel.track(
            'Clause library Show Clause details',
            getClauseLibraryEventProperties('Clause library Show Clause details')
        );
    },
    detailedClauseToggleShowFullDescription: () => {
        mixpanel.track(
            'Detailed clause Toggle show full description',
            getClauseLibraryEventProperties('Detailed clause Toggle show full description')
        );
    },
    detailedClauseToggleShowFullText: () => {
        mixpanel.track(
            'Detailed clause Toggle show full text',
            getClauseLibraryEventProperties('Detailed clause Toggle show full text')
        );
    },
    initializingstepcomplete: () => {
        mixpanel.track(
            'Initializing step complete',
            getScorecardEventProperties('Initializing step complete')
        );
    },
    analyzingstepcomplete: () => {
        mixpanel.track(
            'Analyzing step complete',
            getScorecardEventProperties('Analyzing step complete')
        );
    },
    generatingScorecardStepComplete: () => {
        mixpanel.track(
            'Generating Scorecard step complete',
            getScorecardEventProperties('Generating Scorecard step complete')
        );
    },
    resultsdisplayed: () => {
        mixpanel.track('Results displayed', getScorecardEventProperties('Results displayed'));
    },
    rulecardexpanded: () => {
        mixpanel.track('Rule card expanded', getScorecardEventProperties('Rule card expanded'));
    },
    rulecardcollapsed: () => {
        mixpanel.track('Rule card collapsed', getScorecardEventProperties('Rule card collapsed'));
    },
    ruleCardHoverOnQuestionForTooltip: () => {
        mixpanel.track(
            'Rule card hover on question for tooltip',
            getScorecardEventProperties('Rule card hover on question for tooltip')
        );
    },
    ruleCardHoverOnDescriptionForTooltip: () => {
        mixpanel.track(
            'Rule card hover on description for tooltip',
            getScorecardEventProperties('Rule card hover on description for tooltip')
        );
    },
    ruleCardExtractionsCycleKeyClicked: () => {
        mixpanel.track(
            'Rule card extractions cycle key clicked',
            getScorecardEventProperties('Rule card extractions cycle key clicked')
        );
    },
    searchcontractname: () => {
        mixpanel.track('Search contract name', getContractsEventProperties('Search contract name'));
    },
    searchcontractsolr: () => {
        mixpanel.track('Search contract solr', getContractsEventProperties('Search contract solr'));
    },
    searchcontractclickoncontract: () => {
        mixpanel.track(
            'Search contract click on contract',
            getContractsEventProperties('Search contract click on contract')
        );
    },
    searchcontractdownloadcontract: () => {
        mixpanel.track(
            'Search contract download contract',
            getContractsEventProperties('Search contract download contract')
        );
    },
    searchcontractexporttodocx: () => {
        mixpanel.track(
            'Search contract export to docx',
            getContractsEventProperties('Search contract export to docx')
        );
    },
    searchcontractcopydownloadlink: () => {
        mixpanel.track(
            'Search contract copy download link',
            getContractsEventProperties('Search contract copy download link')
        );
    },
    applyMarkup: () => {
        mixpanel.track('Markup Apply', getMarkupEventProperties('Markup Apply'));
    },
    markupCCDone: () => {
        mixpanel.track('Markup CC done', getMarkupEventProperties('Markup CC done'));
    },
    markupEventsBindingDone: () => {
        mixpanel.track(
            'Markup Events Binding done',
            getMarkupEventProperties('Markup Events Binding done')
        );
    },
    labelexpanded: () => {
        mixpanel.track('Label expanded', getLabelsEventProperties('Label expanded'));
    },
    labelcollapsed: () => {
        mixpanel.track('Label collapsed', getLabelsEventProperties('Label collapsed'));
    },
    labelclickedShowonDocument: () => {
        mixpanel.track(
            'Label clicked Show on Document',
            getLabelsEventProperties('Label clicked Show on Document')
        );
    },
    selectview: () => {
        mixpanel.track('Select view', getLabelsEventProperties('Select view'));
    }

    /** commented functions that could be useful in the future */
    // clauseLibraryFetchSuccess: () => {
    //     mixpanel.track('Clause Library Fetch Success');
    // },
    // clauseLibraryFetchFailure: () => {
    //     mixpanel.track('Clause Library Fetch Failure');
    // },
    // scorecardFetchSuccess: () => {
    //     mixpanel.track('Scorecard Fetch Success');
    // },
    // scorecardFetchFailure: () => {
    //     mixpanel.track('Scorecard Fetch Failure');
    // },
    // insertSuggestedClause: () => {
    //     mixpanel.track('Insert Suggested Clause');
    //     mixpanel.people.increment('All Time Suggested Clause Inserts');
    // },
    // insertClauseFromLibrary: () => {
    //     mixpanel.track('Insert Clause From Library');
    //     mixpanel.people.increment('All Time Clause Inserts');
    // }
};

export default mixpanelEvents;
// "Analyzer Display Mode: Scorecard, Labels,Contract,Clause,Setting,Markup
// Menu click: Scorecard,Labels,Contracts,Clause,Settings,Markup"
// "Analyzer Display Mode: Scorecard, Labels,Contract,Clause,Setting,Markup
// Analyzer # size, nr of characters in contract:
// Analyzer # Scorecard Id: 1,2,3,4

// "
